import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { trackErrorScreen } from 'metrics/user-analytics/errorTracker';

/**
 * Fallback component to handle errors.
 * @param {object} props - resetError, error
 * @param {function} props.resetError - When called it'll remove the Fallback component
 * and render the Sentry ErrorBoundary's children in their initial state
 * @param {Error} props.error - Error caught by the ErrorBoundary, tracked on mount
 * @returns {JSX.Element} React element to display in case of error.
 */
function ErrorFallback({ resetError, error }) {
  useEffect(() => {
    trackErrorScreen({ message: error?.message });
  }, [error]);
  const { t } = useTranslation(['errors']);

  /**
   * Function to navigate back and reset the error.
   * Uses the native window.history to go back one page in the navigation history
   * and calls the resetError function to reset the error state.
   */
  const goBack = () => {
    window.history.back();
    resetError();
  };

  return (
    <div className="h-screen flex flex-col gap-5 items-center justify-center p-6">
      <div className="w-16 h-16">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="80"
          height="80"
          fill="red"
          viewBox="0 0 16 16"
        >
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
        </svg>
      </div>
      <p className="text-xl my-3 font-semibold text-gray-600 text-center">
        {t('something_went_wrong')}
      </p>
      <button
        onClick={goBack}
        className="px-4 py-2 text-gray-600 font-semibold rounded-md border-2 shadow-lg shadow-gray-500/40"
      >
        {t('general:go_back')}
      </button>
    </div>
  );
}

export default ErrorFallback;

ErrorFallback.propTypes = {
  resetError: PropTypes.func.isRequired,
  error: PropTypes.object,
};

ErrorFallback.defaultProps = {
  error: null,
};
